/**
 * Storage adapter for widget/iframe mode that uses multiple fallback strategies
 */

interface StorageAdapter {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
    clear(): void;
}

class WidgetStorage implements StorageAdapter {
    private storage: Storage | null = null;
    private memoryStorage: Map<string, string> = new Map();

    constructor() {
        this.initializeStorage();
    }

    // biome-ignore : Called in constructor
    private initializeStorage() {
        // Try to detect storage availability
        try {
            const testKey = "__widget_storage_test__";
            localStorage.setItem(testKey, "test");
            localStorage.removeItem(testKey);
            this.storage = localStorage;
            // Using localStorage
        } catch (_e) {
            // localStorage not available, using fallbacks
            this.storage = null;
            this.setupParentCommunication();
        }
    }

    private setupParentCommunication() {
        // Set up message listener for parent window communication
        // This allows the widget to communicate with parent window for storage
        // when localStorage is not available
    }

    getItem(key: string): string | null {
        // Try localStorage first
        if (this.storage) {
            try {
                return this.storage.getItem(key);
            } catch (e) {
                console.warn("Failed to get from localStorage:", e);
            }
        }

        // Try sessionStorage
        try {
            const value = sessionStorage.getItem(key);
            if (value !== null) return value;
        } catch (_e) {
            // sessionStorage might also be blocked
        }

        // Try memory storage
        const memValue = this.memoryStorage.get(key);
        if (memValue !== undefined) return memValue;

        // Try parent window communication (async converted to sync with timeout)
        if (window.parent !== window) {
            // For critical data, we could implement a sync mechanism
            // For now, return null and rely on memory storage
        }

        return null;
    }

    setItem(key: string, value: string): void {
        // Try localStorage
        if (this.storage) {
            try {
                this.storage.setItem(key, value);
                return;
            } catch (e) {
                console.warn("Failed to set in localStorage:", e);
            }
        }

        // Try sessionStorage
        try {
            sessionStorage.setItem(key, value);
        } catch (_e) {
            // sessionStorage might also be blocked
        }

        // Always save to memory storage
        this.memoryStorage.set(key, value);

        // Send to parent if in iframe
        if (window.parent !== window) {
            window.parent.postMessage(
                {
                    type: "storage-request",
                    action: "set",
                    key,
                    value,
                },
                "*",
            );
        }
    }

    removeItem(key: string): void {
        // Try all storage methods
        if (this.storage) {
            try {
                this.storage.removeItem(key);
            } catch (_e) {}
        }

        try {
            sessionStorage.removeItem(key);
        } catch (_e) {}

        this.memoryStorage.delete(key);

        // Send to parent if in iframe
        if (window.parent !== window) {
            window.parent.postMessage(
                {
                    type: "storage-request",
                    action: "remove",
                    key,
                },
                "*",
            );
        }
    }

    clear(): void {
        if (this.storage) {
            try {
                this.storage.clear();
            } catch (_e) {}
        }

        try {
            sessionStorage.clear();
        } catch (_e) {}

        this.memoryStorage.clear();

        // Send to parent if in iframe
        if (window.parent !== window) {
            window.parent.postMessage(
                {
                    type: "storage-request",
                    action: "clear",
                },
                "*",
            );
        }
    }
}

// Create singleton instance
const widgetStorage = new WidgetStorage();

/**
 * React hook for widget-safe storage
 */
export function useWidgetStorage<T>(
    key: string,
    initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] {
    // Get initial value
    const getStoredValue = (): T => {
        try {
            const item = widgetStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch {
            return initialValue;
        }
    };

    const [storedValue, setStoredValue] = useState<T>(getStoredValue);

    // Listen for storage changes
    useEffect(() => {
        // Check for updates periodically in widget mode
        const interval = setInterval(() => {
            try {
                const item = widgetStorage.getItem(key);
                const currentValue = item ? JSON.parse(item) : initialValue;
                if (JSON.stringify(currentValue) !== JSON.stringify(storedValue)) {
                    setStoredValue(currentValue);
                }
            } catch {
                // Ignore parse errors
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [key, initialValue, storedValue]);

    // Update function
    const setValue = (value: T | ((val: T) => T)) => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            widgetStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (error) {
            console.error(`Error setting ${key}:`, error);
        }
    };

    return [storedValue, setValue];
}

/**
 * Get storage adapter based on context
 */
export function getStorageAdapter(): StorageAdapter {
    const urlParams = new URLSearchParams(window.location.search);
    const isWidgetMode = urlParams.get("mode") === "widget";

    if (isWidgetMode) {
        return widgetStorage;
    }

    // Use regular localStorage in non-widget mode
    return localStorage;
}

import { useEffect, useState } from "react";

/**
 * Universal storage hook that works in both widget and normal modes
 */
export function useUniversalStorage<T>(
    key: string,
    initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] {
    const urlParams = new URLSearchParams(window.location.search);
    const isWidgetMode = urlParams.get("mode") === "widget";

    // Call both hooks unconditionally to satisfy React's rules of hooks
    const widgetStorageResult = useWidgetStorage(key, initialValue);
    const safeLocalStorageResult = useSafeLocalStorage(key, initialValue);

    // Return the appropriate one based on mode
    return isWidgetMode ? widgetStorageResult : safeLocalStorageResult;
}

// Import the safe localStorage hook
import { useSafeLocalStorage } from "./safeLocalStorage";
